import { useId } from 'react';
import { cx } from '../../utils/cx';
import s from './DatePartsInput.module.css';

export type DateParts = { month: number | null; day: number | null; year: number | null };

export type DatePartsInputProps = {
  value: DateParts;
  onValueChange: (value: DateParts) => void;
  /** Legend, e.g. "When's your birthday?" */
  label: string;
  /** Show the optional year field. */
  withYear?: boolean;
  error?: string;
  className?: string;
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Days in a month; February allows 29 (a birthday can be Feb 29) unless the year says otherwise. */
export function daysInMonth(month: number, year: number | null): number {
  if (month === 2)
    return year && !((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) ? 28 : 29;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** Month + day (+ optional year) for birthdays and yearly dates. Native selects: best on phones. */
export function DatePartsInput({
  value,
  onValueChange,
  label,
  withYear = true,
  error,
  className,
}: DatePartsInputProps) {
  const id = useId();
  const maxDay = value.month ? daysInMonth(value.month, value.year) : 31;
  const set = (patch: Partial<DateParts>) => {
    const next = { ...value, ...patch };
    if (next.month && next.day && next.day > daysInMonth(next.month, next.year)) next.day = null;
    onValueChange(next);
  };
  const thisYear = new Date().getFullYear();

  return (
    <fieldset
      className={cx(s.fieldset, className)}
      aria-describedby={error ? `${id}-error` : undefined}
    >
      <legend className={s.legend}>{label}</legend>
      <div className={s.row}>
        <label className={s.part}>
          <span className={s.partLabel}>Month</span>
          <select
            className={s.select}
            value={value.month ?? ''}
            aria-invalid={error ? true : undefined}
            onChange={(e) =>
              set({ month: e.currentTarget.value ? Number(e.currentTarget.value) : null })
            }
          >
            <option value="">Month</option>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className={s.part} data-size="sm">
          <span className={s.partLabel}>Day</span>
          <select
            className={s.select}
            value={value.day ?? ''}
            aria-invalid={error ? true : undefined}
            onChange={(e) =>
              set({ day: e.currentTarget.value ? Number(e.currentTarget.value) : null })
            }
          >
            <option value="">Day</option>
            {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        {withYear ? (
          <label className={s.part} data-size="sm">
            <span className={s.partLabel}>
              Year <span className={s.optional}>(optional)</span>
            </span>
            <input
              className={s.select}
              type="number"
              inputMode="numeric"
              min={1900}
              max={thisYear}
              placeholder="YYYY"
              value={value.year ?? ''}
              onChange={(e) => {
                const n = e.currentTarget.valueAsNumber;
                set({ year: Number.isNaN(n) ? null : n });
              }}
            />
          </label>
        ) : null}
      </div>
      {error ? (
        <p id={`${id}-error`} className={s.error} role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
